import React, { forwardRef, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { CopyButton } from 'sutra/components/Text/CopyButton'
import {
  codeStyles,
  lineClamp,
  textSizeStyles,
  textToneStyles,
} from 'sutra/components/Text/text-styles'
import { useIsClamped } from 'sutra/components/Text/use-is-clamped'
import { typography } from 'sutra/tokens/typography'

export type TextSize = keyof typeof textSizeStyles
export type TextTone = keyof typeof textToneStyles

export interface TextCopyable {
  // Defaults to the rendered text.
  text?: string
  onCopy?: () => void
}

export interface TextProps
  extends
    React.HTMLAttributes<HTMLElement>,
    Pick<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      'href' | 'target' | 'rel'
    > {
  $size?: TextSize
  $tone?: TextTone
  $strong?: boolean
  $code?: boolean
  // 1 truncates to a single line; n > 1 clamps to n lines.
  $lineClamp?: number
  // `p` by default; use `span` for inline text and `a` (with `$tone="link"`)
  // for a link.
  as?: React.ElementType
  // Adds a copy button after the text.
  copyable?: boolean | TextCopyable
  // With `$lineClamp`: adds a "more" button that lifts the clamp. Shown only
  // while text is actually cut off.
  expandable?: boolean
}

interface StyledTextProps {
  $size: TextSize
  $tone: TextTone
  $strong: boolean
  $code: boolean
  $lineClamp?: number
  $adorned: boolean
}

const StyledText = styled.p<StyledTextProps>`
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: ${({ $strong }) =>
    $strong ? typography.weights.semibold : typography.weights.regular};
  margin: 0;
  ${({ $size }) => textSizeStyles[$size]}
  ${({ $tone }) => textToneStyles[$tone]}
  ${({ $code }) => $code && codeStyles}
  ${({ $adorned, $lineClamp }) =>
    $adorned
      ? css`
          display: inline-flex;
          align-items: flex-end;
          gap: 4px;
          max-width: 100%;
        `
      : $lineClamp && lineClamp($lineClamp)}
`

// Holds the clamp when a copy or "more" button sits beside the text, so the
// button is never clipped along with it.
const Body = styled.span<{ $lineClamp?: number }>`
  flex: 0 1 auto;
  min-width: 0;
  ${({ $lineClamp }) => $lineClamp && lineClamp($lineClamp)}
`

const MoreButton = styled.button`
  flex: none;
  padding: 0;
  color: inherit;
  font: inherit;
  text-decoration: underline;
  background: none;
  border: 0;
  cursor: pointer;
`

export const Text = forwardRef<HTMLParagraphElement, TextProps>(function Text(
  {
    $size = 'md',
    $tone = 'default',
    $strong = false,
    $code = false,
    $lineClamp,
    as,
    copyable,
    expandable = false,
    children,
    ...rest
  },
  ref,
) {
  const [expanded, setExpanded] = useState(false)
  const bodyRef = useRef<HTMLSpanElement>(null)

  const adorned = Boolean(copyable) || expandable
  const clamp = expanded ? undefined : $lineClamp
  const isClamped = useIsClamped(bodyRef, expandable && !expanded, children)
  const copyOptions = typeof copyable === 'object' ? copyable : undefined

  return (
    <StyledText
      {...rest}
      ref={ref}
      as={as}
      $size={$size}
      $tone={$tone}
      $strong={$strong}
      $code={$code}
      $lineClamp={clamp}
      $adorned={adorned}
    >
      {adorned ? (
        <>
          <Body ref={bodyRef} $lineClamp={clamp}>
            {children}
          </Body>
          {expandable && isClamped && (
            <MoreButton type="button" onClick={() => setExpanded(true)}>
              more
            </MoreButton>
          )}
          {copyable && (
            <CopyButton
              getText={() =>
                copyOptions?.text ?? bodyRef.current?.textContent ?? ''
              }
              onCopy={copyOptions?.onCopy}
            />
          )}
        </>
      ) : (
        children
      )}
    </StyledText>
  )
})

Text.displayName = 'Text'
